/**
 * shortcutHelpers.js — 모바일 헤더 바로가기 (2026-04-27, DB 전환)
 *
 * DB user_preferences (key='mobile_shortcuts') 에 사용자별 path 배열 저장.
 * 디바이스 무관 동기화. 위장 접속 시 위장된 사용자 설정.
 */
import { Layers } from 'lucide-react';
import { apiClient } from '../../../api/apiClient';
import { MENU_GROUPS, ADMIN_ONLY_MENUS, MY_MENUS, buildExtCallMenus } from './menuConfig';
import { COMPANY_MENU_FEATURES } from './companyFeatures';
import { INTEGRATED_HOME_PATH, displayMenuName, integratedMenuPlace } from './integratedScope';

export const MAX_SHORTCUTS = 5;
const KEY = 'mobile_shortcuts';

// 메뉴에서 빠졌지만 저장된 바로가기에 남아 있을 수 있는 예전 주소 → 지금 메뉴 주소.
//   출퇴근 달력 → 출퇴근 관리 [달력] 탭(2026-09-29). **불러올 때** 바꿔서(중복 제거) 상단 버튼·편집 화면이
//   같은 주소를 보게 한다 — 그리기만 바꾸면 편집 화면에서 그 바로가기를 끌 수 없었다(검토에서 발견).
//   다음에 저장하면 바뀐 주소로 저장된다.
const LEGACY_SHORTCUT_PATHS = {
  '/admin/attendance-calendar': '/admin/attendance',
};

export function normalizeShortcuts(paths) {
  const out = [];
  for (const p of paths || []) {
    const q = LEGACY_SHORTCUT_PATHS[p] || p;
    if (q && !out.includes(q)) out.push(q);
  }
  return out.slice(0, MAX_SHORTCUTS);
}

export async function loadShortcuts() {
  const r = await apiClient.get(`/api/my/preferences/${KEY}`);
  if (!r.ok) return [];
  const v = r.data?.value;
  return Array.isArray(v) ? normalizeShortcuts(v) : [];
}

export async function saveShortcuts(paths) {
  const trimmed = (paths || []).slice(0, MAX_SHORTCUTS);
  await apiClient.put(`/api/my/preferences/${KEY}`, { value: trimmed });
}

// path → { to, label, Icon } 검색 (정적 메뉴만)
export function findMenuByPath(path) {
  if (!path) return null;
  for (const m of MY_MENUS) if (m.to === path) return m;
  for (const g of MENU_GROUPS) for (const m of g.items) if (m.to === path) return m;
  for (const m of ADMIN_ONLY_MENUS) if (m.to === path) return m;
  return null;
}

const upper = (v) => String(v || '').trim().toUpperCase();

/**
 * 휴대폰 위쪽 막대 바로가기: 주소 → { to, label, Icon }. 못 찾으면 null(막대에 그리지 않는다).
 * 2026-10-24 사용자: "모바일은 통합관리에 있는 카테고리가 즐겨찾기가 안되는데" — 전엔 정적 메뉴표만 찾아서
 *   통합 메뉴는 고르지 못하게 막아 두었고(editable=false), 회사 메뉴·회사별 일일콜은 고를 수는 있는데 막대에 안 나왔다.
 *   정적 메뉴는 종전 그대로. 나머지는 **지금 이 사람에게 보이는 것만**(열쇠가 빠지면 막대에서도 숨는다):
 *   통합 메뉴 = 작업 대상 통합 메뉴 목록 / 회사 메뉴 = 작업 대상 회사 + 사이드바와 같은 노출 조건 / 회사별 일일콜 = 내 열쇠.
 */
export function resolveShortcut(path, { scope = null, permCodes = null } = {}) {
  const p = String(path || '');
  const base = findMenuByPath(p);
  if (base) return base;
  if (p === INTEGRATED_HOME_PATH) return scope?.canUseIntegrated ? { to: p, label: '통합 관리 홈', Icon: Layers } : null;
  const im = p.match(/^\/admin\/integrated\/(\d+)$/);
  if (im) {
    const menu = (scope?.integratedMenus || []).find((m) => String(m.id) === im[1]);
    return menu ? { to: p, label: displayMenuName(menu.name), Icon: integratedMenuPlace(menu, MENU_GROUPS).Icon || Layers } : null;
  }
  const em = p.match(/^\/admin\/ext\/([^/]+)\/(.+)$/);
  if (em) {
    const co = (scope?.companies || []).find((c) => upper(c.code) === upper(em[1]));
    const f = COMPANY_MENU_FEATURES.find((x) => x.path === em[2]);
    // 회사 메뉴 재료가 없으면(일반 직원 — enabled_features null) 그리지 않는다. 사이드바 가드와 같다(menuComposition).
    if (!co || !f || !co.enabled_features || !f.show(co.enabled_features, co.accessible_features ?? null)) return null;
    return { to: p, label: `${f.label} (${co.name || co.code})`, Icon: f.Icon };
  }
  return buildExtCallMenus(permCodes).find((m) => m.to === p) || null;
}
